import { Alert } from '@/components/ui/alert';
import { Text, VStack } from '@chakra-ui/react';
import {
  MentionBoostReadinessBlockerCode,
  type MentionBoostReadinessResponse,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { ConnectMetaAdsButton } from './ConnectMetaAdsButton';

/**
 * Ads-side readiness blockers this notice explains, in display priority. It
 * covers only the cases where the brand has no account to select at all.
 *
 * Two blockers are owned elsewhere and must not be listed here: the picker
 * handles AD_ACCOUNT_SELECTION_REQUIRED, and AdAccountPaymentNotice owns
 * AD_ACCOUNT_PAYMENT_MISSING, which it explains against the selected account
 * with a link to that account's Meta billing settings.
 */
const NOTICE_BLOCKERS = [
  MentionBoostReadinessBlockerCode.META_ADS_NOT_CONNECTED,
  MentionBoostReadinessBlockerCode.AD_ACCOUNT_VERIFICATION_FAILED,
  MentionBoostReadinessBlockerCode.AD_ACCOUNT_MISSING,
  MentionBoostReadinessBlockerCode.AD_ACCOUNT_DISABLED,
  MentionBoostReadinessBlockerCode.AD_ACCOUNT_CURRENCY_MISMATCH,
] as const;

function resolveBlockerCode(
  readiness: MentionBoostReadinessResponse | null
): MentionBoostReadinessBlockerCode {
  const codes = new Set(readiness?.blockers.map((blocker) => blocker.code));
  // Default to "not connected": if there is no usable ad account and no codified
  // blocker, the honest assumption is that ads access was never granted.
  return (
    NOTICE_BLOCKERS.find((code) => codes.has(code)) ??
    MentionBoostReadinessBlockerCode.META_ADS_NOT_CONNECTED
  );
}

/**
 * Honest blocker shown in the boost setup when the brand cannot launch because
 * ad-account access is missing or its currency does not match the brand. For
 * the not-connected case it routes to the integrations page where the Business
 * Login dialog grants ad access; the other cases need clear guidance only.
 */
export function BoostReadinessNotice({
  readiness,
}: {
  readiness: MentionBoostReadinessResponse | null;
}) {
  const { t } = useTranslation();

  const code = resolveBlockerCode(readiness);
  const canConnect = code === MentionBoostReadinessBlockerCode.META_ADS_NOT_CONNECTED;

  return (
    <Alert status="warning" variant="surface" title={t(`mentions.boost.readiness.${code}.title`)}>
      <VStack align="stretch" gap={2}>
        <Text>{t(`mentions.boost.readiness.${code}.description`)}</Text>
        {canConnect && <ConnectMetaAdsButton />}
      </VStack>
    </Alert>
  );
}
